<template>
  <div :class="['question-inset', { standalone: isStandalone }]">
    <div class="avatar-wrapper">
      <UserAvatar
        :image="
          $helpers.getProfilePictureThumbnailUrl(question.person.profilePhotoId)
        "
      />
    </div>

    <div class="content-wrapper">
      <div class="top-line">
        <h3 class="question-title" :title="question.title">
          <NuxtLink
            :to="`/szakivalaszol/${question.seoName}`"
            class="question-link"
            @click="$trackers.trackClick('QuestionInsetTitle')"
          >
            {{ question.title }}
          </NuxtLink>
        </h3>
        <div class="badges is-hidden-mobile">
          <NuxtLink
            v-if="getQuestionProfession"
            class="badge"
            :to="
              localePath({
                name: 'professionQuestions',
                params: {
                  profession: getQuestionProfession.seoName,
                },
              })
            "
            @click="$trackers.trackClick('QuestionInsetProfession')"
          >
            <div>
              {{ getQuestionProfession.name }}
            </div>
          </NuxtLink>
          <NuxtLink
            v-if="question.professionTask"
            class="badge secondary"
            :to="
              localePath({
                name: 'professionTaskQuestions',
                params: {
                  professionTask: question.professionTask.seoName,
                },
              })
            "
            @click="$trackers.trackClick('QuestionInsetProfessionTask')"
          >
            <div>
              {{ question.professionTask.name }}
            </div>
          </NuxtLink>
        </div>
        <div
          v-if="showClose"
          class="pointer h-full p-2 ml-2 flex items-center justify-center"
          @click="close"
        >
          <IconComponent icon="times" />
        </div>
      </div>

      <p class="question-body">
        {{ question.body }}
      </p>

      <div class="flex items-center justify-between mt-4">
        <p class="text-xs font-normal text-black/50">
          {{ formatDate(question.created) }}
        </p>
        <NuxtLink
          :to="`/szakivalaszol/${question.seoName}`"
          @click="$trackers.trackClick('QuestionInsetAnswerers')"
        >
          <div
            class="flex [&>*:not(:first-child)]:-ml-2.5"
            :title="
              $t('questionsPage.inset.answerersTooltip', {
                answererCount: question.answererCount,
              })
            "
          >
            <div v-for="answerer in answerers" :key="answerer.id" class="h-9">
              <UserAvatar
                :image="
                  $helpers.getProfilePictureThumbnailUrl(
                    answerer.profilePhotoId
                  )
                "
                :is-frameless="true"
              />
            </div>
            <div v-if="question.answererCount > 2" class="h-9">
              <UserAvatar
                :text="`+${question.answererCount - 2}`"
                :is-frameless="true"
              />
            </div>
          </div>
        </NuxtLink>
      </div>

      <div v-if="showAnswerButton">
        <NuxtLink
          class="button is-strong"
          :to="`/szakivalaszol/${question.seoName}`"
          @click="$trackers.trackClick('QuestionInsetAnswerButton')"
        >
          {{ $t("questionsPage.inset.answerButton") }}
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<script>
import { differenceToNow } from "~/helpers/date";
import UserAvatar from "~/components/UserAvatar";

export default {
  components: { UserAvatar },
  props: {
    question: {
      type: Object,
      default() {
        return {};
      },
    },
    isStandalone: {
      type: Boolean,
      default: false,
    },
    forceProfession: {
      type: Boolean,
      default: false,
    },
    showClose: {
      type: Boolean,
      default: false,
    },
    showAnswerButton: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    answerers() {
      if (this.question.answerers) {
        return this.question.answerers.slice(0, 2);
      }
      return [];
    },
    getQuestionProfession() {
      // console.info('question professions', this.question.professionTask)
      if (this.question?.profession) {
        return this.question.profession;
      } else if (
        this.forceProfession &&
        this.question?.professionTask?.professions?.length > 0
      ) {
        return this.question.professionTask.professions[0];
      } else {
        return null;
      }
    },
  },
  methods: {
    formatDate(date) {
      return differenceToNow(this, date);
    },
    close() {
      this.$trackers.trackClick("QuestionInsetClose");
      this.$emit("close", this.question.id);
    },
  },
};
</script>

<style lang="scss" scoped>
@use "styles/_all-members" as global;

$spacing: 10px;
$line-height: 22px;

.question-inset {
  display: flex;
  padding: $spacing * 1.5;
  font-size: 14px;

  &:not(:last-of-type) {
    border-bottom: 1px solid #dfdfdf;
  }

  &.standalone {
    border: none;
    border-radius: $spacing;
    box-shadow: 2px 2px 9px rgba(0, 0, 0, 0.05);
    background: #fff;

    &:not(:last-of-type) {
      margin-bottom: $spacing * 2.5;
    }
  }
}
.avatar-wrapper {
  margin-right: $spacing * 1.5;

  &::v-deep {
    .avatar-container {
      position: relative;
      top: 3px;
      transform: scale(1.1);
    }
  }
}
.content-wrapper {
  flex: 1;
}
.top-line {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}
.question-title {
  @include global.line-clamp();
  flex: 1;
  margin-bottom: $spacing;
  font-size: 18px;
  font-weight: bold;

  @include global.mobile {
    font-size: 16px;
  }
}
.question-link {
  color: global.$primary;

  &:focus,
  &:hover {
    color: darken(global.$primary, 10%);
  }
  &:active {
    color: darken(global.$primary, 15%);
  }
}
.badges {
  display: flex;
  margin-left: $spacing * 1.5;
}
.badge {
  text-transform: lowercase;
  line-height: $line-height;
  border-radius: $spacing;
  border: 1px solid rgba(40, 190, 255, 1);
  padding: 0 12px;
  font-size: 14px;
  font-weight: 800;
  color: rgba(40, 190, 255, 1);

  &.secondary {
    border-color: rgba(255, 156, 40, 1);
    color: rgba(255, 156, 40, 1);
  }
  &:not(:last-child) {
    margin-right: 8px;
  }
}
.question-body {
  @include global.line-clamp(2);
  line-height: $line-height;
  word-break: break-word;
}
</style>
